/**
 * useBattleHistory Hook
 *
 * React hook for fetching user's completed battle history with stats.
 * Provides loading state, error handling, and refetch capability.
 */

import { useState, useEffect, useCallback } from 'react';
import { getBattleHistory } from '../services/battle-api';
import { useAuth } from '../contexts/AuthContext';

/**
 * Single battle entry from history
 */
export interface BattleHistoryEntry {
  battleId: string;
  battleName: string;
  completedAt: string | null;
  opponentDisplayName: string;
  userResult: 'win' | 'loss' | 'draw';
  resultReason?: string;
}

/**
 * Battle stats summary
 */
export interface BattleStats {
  wins: number;
  losses: number;
  draws: number;
}

/**
 * Hook return type
 */
export interface UseBattleHistoryResult {
  /** Array of completed battles */
  battles: BattleHistoryEntry[];
  /** Win/loss/draw statistics */
  stats: BattleStats;
  /** Whether data is being fetched */
  isLoading: boolean;
  /** Error message if fetch failed */
  error: string | null;
  /** Function to refetch battle history */
  refetch: () => void;
}

/**
 * useBattleHistory Hook
 *
 * Fetches and manages user's battle history state.
 *
 * @param limit - Maximum number of battles to fetch (default: 10)
 * @returns Object with battles, stats, loading state, error, and refetch function
 *
 * @example
 * ```typescript
 * function RecentBattlesSection() {
 *   const { battles, stats, isLoading, error, refetch } = useBattleHistory(5);
 *
 *   if (isLoading) return <Spinner />;
 *   if (error) return <Error message={error} />;
 *
 *   return (
 *     <div>
 *       <h3>Record: {stats.wins}W / {stats.losses}L / {stats.draws}D</h3>
 *       <ul>
 *         {battles.map(battle => (
 *           <li key={battle.battleId}>
 *             {battle.battleName} vs {battle.opponentDisplayName} - {battle.userResult}
 *           </li>
 *         ))}
 *       </ul>
 *       <button onClick={refetch}>Refresh</button>
 *     </div>
 *   );
 * }
 * ```
 */
export function useBattleHistory(limit: number = 10): UseBattleHistoryResult {
  const { user, loading: authLoading } = useAuth();
  const [battles, setBattles] = useState<BattleHistoryEntry[]>([]);
  const [stats, setStats] = useState<BattleStats>({ wins: 0, losses: 0, draws: 0 });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchHistory = useCallback(async () => {
    // Wait for auth to be ready
    if (authLoading || !user) {
      return;
    }

    // Small delay to ensure __getAuthToken is set by AuthContext's useEffect
    await new Promise(resolve => setTimeout(resolve, 50));

    setIsLoading(true);
    setError(null);

    try {
      const response = await getBattleHistory(limit);
      setBattles(response.battles);
      setStats(response.stats);
    } catch (err) {
      if (err instanceof Error) {
        setError(err.message);
      } else {
        setError('Failed to fetch battle history');
      }
    } finally {
      setIsLoading(false);
    }
  }, [limit, user, authLoading]);

  // Fetch when auth is ready and limit changes
  useEffect(() => {
    if (!authLoading && user) {
      fetchHistory();
    } else if (!authLoading && !user) {
      // Not logged in
      setIsLoading(false);
      setError('Please log in to view battle history');
    }
  }, [fetchHistory, user, authLoading]);

  return {
    battles,
    stats,
    isLoading,
    error,
    refetch: fetchHistory
  };
}
